import { useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query'
import { createApiCall } from 'services'
import { SIGNUPS_QUERY_KEY } from './useSignups'

/**
 * ⚠️ `...options` GOES FIRST in both hooks below. An object literal's last key
 * wins, so spreading the caller's options AFTER the `onSuccess` wrapper replaced
 * it outright and took `invalidateQueries` with it — silently, for every caller
 * that passes a success toast.
 *
 * Both callers on the Signups page do. Found and measured while fixing the same
 * defect in `usePricingAccess.ts`; see that file's docblock for the browser
 * evidence. `useGrantSignupByEmail`'s caller had grown a manual `refetchSignups()`
 * in its own `onSuccess`, which is what kept the symptom hidden here.
 */

export type GrantSignupByIdPayload = {
  signupId: string
  /** VRG-133: true → "Allow & invite" (allowlist + provision + WorkOS invite); false/undefined → "Allow" (allowlist only). */
  invite?: boolean
}

export type GrantSignupByEmailPayload = {
  email: string
  name?: string
  invite?: boolean
}

export type GrantSignupResponse = {
  signupEntry: Record<string, unknown>
  companyId: string | null
}

// Grant a specific waitlist row by its ID
export const useGrantSignup = (
  options?: Omit<UseMutationOptions<GrantSignupResponse, Error, GrantSignupByIdPayload>, 'mutationFn'>
) => {
  const queryClient = useQueryClient()

  return useMutation<GrantSignupResponse, Error, GrantSignupByIdPayload>({
    mutationFn: async ({ signupId, invite }) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/internal/signups/${signupId}/grant`,
        data: { invite: !!invite },
      })
      if (response.status >= 400) {
        throw new Error(response.data?.message || 'Failed to grant access')
      }
      return response.data as GrantSignupResponse
    },
    // ⚠️ Spread FIRST — see the note above the hooks.
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [SIGNUPS_QUERY_KEY] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}

// Grant access by email directly (not via a waitlist row)
export const useGrantSignupByEmail = (
  options?: Omit<UseMutationOptions<GrantSignupResponse, Error, GrantSignupByEmailPayload>, 'mutationFn'>
) => {
  const queryClient = useQueryClient()

  return useMutation<GrantSignupResponse, Error, GrantSignupByEmailPayload>({
    mutationFn: async (payload) => {
      const response = await createApiCall({
        method: 'POST',
        url: '/internal/signups/grant-email',
        data: payload,
      })
      if (response.status >= 400) {
        throw new Error(response.data?.message || 'Failed to grant access')
      }
      return response.data as GrantSignupResponse
    },
    // ⚠️ Spread FIRST — see the note above the hooks.
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [SIGNUPS_QUERY_KEY] })
      options?.onSuccess?.(data, variables, context)
    },
  })
}
